import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Dropdown from 'react-bootstrap/Dropdown';
import type { CurrentUser } from '@/api/types';
import { useAuth } from '@/auth/useAuth';
import { useLayout } from '@/theme/useLayout';
import { Avatar } from './Avatar';
import { LanguageMenu } from './LanguageMenu';

/**
 * The signed-in user's header dropdown.
 *
 * The Razor version placed the toggle and `.dropdown-menu` as direct children of
 * `header.app-header`; react-bootstrap wraps them in a `div.dropdown`. That is
 * safe here because `.app-header` lays its children out with `display: flex` and
 * `gap` and selects none of them by position (`_layout.scss`). The adjacency the
 * reveal *does* depend on — `.show + .dropdown-menu-animated` — is preserved,
 * since the menu is still the toggle's immediate next sibling.
 *
 * Logout goes through the API rather than a form POST to `/auth/logout`, so the
 * cached session and query data are cleared in the same step.
 */
export function ProfileMenu({ user }: { user: CurrentUser }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { isFullscreen, toggleFullscreen } = useLayout();

  const handleLogout = async () => {
    await logout();
    navigate('/auth-login', { replace: true });
  };

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        as="button"
        type="button"
        title={user.email}
        className="btn-system bg-transparent no-arrow d-flex flex-shrink-0 align-items-center justify-content-center"
        aria-label={t('OpenProfileMenu', 'Open profile menu')}
      >
        <Avatar user={user} sizeClass="profile-image-md" />
      </Dropdown.Toggle>

      <Dropdown.Menu renderOnMount className="dropdown-menu-animated">
        <div className="notification-header rounded-top mb-2">
          <div className="d-flex flex-row align-items-center mt-1 mb-1 color-white">
            <span className="status status-success d-inline-block me-2">
              <Avatar user={user} initialsClass="fw-bold fs-sm" />
            </span>
            <div className="info-card-text">
              <div className="fs-lg text-truncate text-truncate-lg">{user.displayName}</div>
              <span className="text-truncate text-truncate-md opacity-80 fs-sm">{user.email}</span>
              {user.jobTitle && (
                <span className="text-truncate text-truncate-md opacity-80 fs-xs">{user.jobTitle}</span>
              )}
            </div>
          </div>
        </div>

        <div className="dropdown-divider m-0" />

        <Dropdown.Item as={Link} to="/user-profile" className="d-flex align-items-center">
          <i className="bi bi-person me-2" />
          <span>{t('Profile', 'Profile')}</span>
        </Dropdown.Item>

        <div className="dropdown-divider m-0" />

        <Dropdown.Item
          as="button"
          type="button"
          className="d-flex justify-content-between align-items-center"
          aria-pressed={isFullscreen}
          onClick={toggleFullscreen}
        >
          <span className="d-flex align-items-center">
            <i className="bi bi-arrows-fullscreen me-2" />
            <span>{t('Fullscreen', 'Fullscreen')}</span>
          </span>
          <b className="text-muted fs-nano px-2 rounded font-monospace align-self-center border">F11</b>
        </Dropdown.Item>

        <LanguageMenu />

        <div className="dropdown-divider m-0" />

        <Dropdown.Item as={Link} to="/auth-changepassword" className="d-flex align-items-center">
          <i className="bi bi-key me-2" />
          <span>{t('ChangePassword', 'Change password')}</span>
        </Dropdown.Item>

        <Dropdown.Item
          as="button"
          type="button"
          className="py-3 fw-500 d-flex justify-content-between align-items-center text-danger"
          onClick={() => void handleLogout()}
        >
          <span className="d-flex align-items-center">
            <i className="bi bi-box-arrow-right me-2" />
            <span>{t('Logout', 'Logout')}</span>
          </span>
          <span className="d-block text-truncate text-truncate-sm ms-1 opacity-75">{user.userName}</span>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
